import { DataType, PreviewRow, SchemaColumn, SchemaTable } from "./types";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function inferDataType(key: string): DataType {
  const k = key.toLowerCase();
  if (
    /date$|^date_|_date_|doj$|dol$|resignation$|accept_by$|approved_date$|offered_date$|created_date$|cashout_date$|cancellation_date$/.test(
      k
    )
  )
    return "date";
  if (/price|amount|value|payable|proceeds|perquisite|_inr$/.test(k))
    return "currency";
  if (/percentage|percent$/.test(k)) return "percentage";
  if (
    /options$|shares$|units$|vested$|exercised$|granted$|cashed_out$|lapsed$|forfeited$|forefited$|cancelled$|outstanding$|active_vested$|active_unvested$|sold_options$|sold_shares$|net_vested$|total_units$|total_options$|total_cancelled$|_number$|s_no$|sr_no$|no_of_|period_complete$/.test(
      k
    )
  )
    return "number";
  return "string";
}

function col(
  tableId: string,
  key: string,
  label: string,
  overrideType?: DataType,
  description?: string
): SchemaColumn {
  return {
    id: `${tableId}.${key}`,
    name: key,
    displayName: label,
    dataType: overrideType ?? inferDataType(key),
    description: description ?? label,
    sourceTable: tableId,
    isCustom: false,
  };
}

function table(
  id: string,
  displayName: string,
  description: string,
  columns: SchemaColumn[]
): SchemaTable {
  return { id, name: id, displayName, sourceType: "sql", columns };
}

// ─── Schema Catalog ──────────────────────────────────────────────────────────
//
// Unified flat schema derived from base tables:
//   Employee + EmployeeMetaData + OptionsPlan + Grant + computed columns
//
// Columns are grouped by entity for clarity. The backend resolves every column
// key regardless of which grain (grant / employee / plan / vesting_tranche /
// transaction) the sheet uses — numeric columns are aggregated automatically.
//
// ─────────────────────────────────────────────────────────────────────────────

export const MOCK_SCHEMA: SchemaTable[] = [
  // ── Employee ──────────────────────────────────────────────────────────────
  table("employee", "Employee", "Core employee details", [
    col(
      "employee",
      "employee_name",
      "Employee Name",
      "string",
      "Full legal name of the employee as registered in the system."
    ),
    col(
      "employee",
      "hr_id",
      "HR ID / Employee ID",
      "string",
      "Unique identifier assigned by HR / payroll — matches your HRMS."
    ),
    col(
      "employee",
      "email",
      "Work Email",
      "string",
      "Company email address of the employee."
    ),
    col(
      "employee",
      "status",
      "Employee Status",
      "string",
      "Current employment status: Active, Resigned, Deactivated, etc."
    ),
    col(
      "employee",
      "employment_type",
      "Employment Type",
      "string",
      "Nature of employment: Full-time, Part-time, Contractor, etc."
    ),
    col(
      "employee",
      "date_of_joining",
      "Date of Joining",
      "date",
      "The date the employee joined the company."
    ),
    col(
      "employee",
      "resignation_date",
      "Resignation Date",
      "date",
      "Date the employee submitted their resignation, if applicable."
    ),
    col(
      "employee",
      "last_day",
      "Last Working Day",
      "date",
      "The employee's last day at the company (on resignation/termination)."
    ),
    col(
      "employee",
      "is_login_provided",
      "Login Provided",
      "boolean",
      "Whether the employee has been given portal login access."
    ),
    col(
      "employee",
      "is_deactivated",
      "Is Deactivated",
      "boolean",
      "True if the employee record has been deactivated."
    ),
    col(
      "employee",
      "tenure_days",
      "Tenure (Days)",
      "number",
      "Number of days since the employee joined (as of today)."
    ),
    col(
      "employee",
      "tenure_months",
      "Tenure (Months)",
      "number",
      "Number of complete months since the employee joined."
    ),
  ]),

  // ── Employee Metadata ─────────────────────────────────────────────────────
  table(
    "employee_meta",
    "Employee Details",
    "Extended employee profile — department, role, PII fields",
    [
      col(
        "employee_meta",
        "department",
        "Department",
        "string",
        "The team or business unit the employee belongs to."
      ),
      col(
        "employee_meta",
        "designation",
        "Designation",
        "string",
        "Job title or role of the employee."
      ),
      col(
        "employee_meta",
        "manager",
        "Manager",
        "string",
        "Reporting manager's name."
      ),
      col(
        "employee_meta",
        "nationality",
        "Nationality",
        "string",
        "Employee's nationality / citizenship."
      ),
      col(
        "employee_meta",
        "working_country",
        "Working Country",
        "string",
        "Country where the employee is currently based."
      ),
      col(
        "employee_meta",
        "residential_status",
        "Residential Status",
        "string",
        "Tax residential status — Resident, Non-Resident, etc."
      ),
      col(
        "employee_meta",
        "legal_name",
        "Legal Name",
        "string",
        "Legal name as it appears on government-issued documents. (PII)"
      ),
      col(
        "employee_meta",
        "pan",
        "PAN",
        "string",
        "Permanent Account Number used for tax purposes in India. (PII)"
      ),
      col(
        "employee_meta",
        "contact",
        "Contact Number",
        "string",
        "Mobile or work phone number. (PII)"
      ),
      col(
        "employee_meta",
        "personal_email",
        "Personal Email",
        "string",
        "Personal (non-work) email address. (PII)"
      ),
      col(
        "employee_meta",
        "address",
        "Address",
        "string",
        "Residential address. (PII)"
      ),
      col(
        "employee_meta",
        "note",
        "Notes",
        "string",
        "Free-text notes added by HR about this employee."
      ),
    ]
  ),

  // ── Plan ──────────────────────────────────────────────────────────────────
  table("plan", "Plan", "ESOP / RSU plan details from OptionsPlan", [
    col(
      "plan",
      "plan_name",
      "Plan Name",
      "string",
      "Name of the ESOP / RSU / SAR plan."
    ),
    col(
      "plan",
      "plan_type",
      "Plan Type",
      "string",
      "Legal plan type — ESOP, RSU, SAR, etc."
    ),
    col(
      "plan",
      "plan_unit_type",
      "Unit Type",
      "string",
      "The unit of compensation granted: ESOP, RSU, or SAR."
    ),
    col(
      "plan",
      "plan_unit_name",
      "Unit Name",
      "string",
      "Custom label for the unit (e.g. 'options', 'shares')."
    ),
    col(
      "plan",
      "plan_state",
      "Plan State",
      "string",
      "Lifecycle state of the plan: ACTIVE, INACTIVE, etc."
    ),
    col(
      "plan",
      "plan_pool_size",
      "Total Pool Size",
      "number",
      "Maximum options that can be granted under this plan."
    ),
    col(
      "plan",
      "plan_start_date",
      "Plan Start Date",
      "date",
      "Date from which the plan became effective."
    ),
    col(
      "plan",
      "plan_term",
      "Plan Term (Years)",
      "number",
      "Duration for which the plan is valid, in years."
    ),
    col(
      "plan",
      "plan_scope",
      "Plan Scope",
      "string",
      "Whether the plan is for EMPLOYEES, DIRECTORS, or ALL."
    ),
    col(
      "plan",
      "plan_conversion_ratio",
      "Conversion Ratio",
      "number",
      "Number of options required to convert to one share."
    ),
    col(
      "plan",
      "plan_is_fractional",
      "Fractional Units",
      "boolean",
      "Whether fractional vesting tranches are allowed."
    ),
    col(
      "plan",
      "plan_is_managed_by_trust",
      "Managed by Trust",
      "boolean",
      "Whether the option pool is held in an employee benefit trust."
    ),
    col(
      "plan",
      "plan_forfeiture_settings",
      "Forfeiture Settings",
      "string",
      "JSON describing what happens to unvested options on exit."
    ),
  ]),

  // ── Grant ─────────────────────────────────────────────────────────────────
  table("grant", "Grant", "All fields on a grant record", [
    col(
      "grant",
      "grant_identifier",
      "Grant Identifier",
      "string",
      "Unique internal ID for this grant (e.g. GRT-001)."
    ),
    col(
      "grant",
      "grant_type",
      "Grant Type",
      "string",
      "Type of grant: ESOP, RSU, SAR, etc."
    ),
    col(
      "grant",
      "grant_date",
      "Grant Date",
      "date",
      "The date on which the grant was made / offered to the employee."
    ),
    col(
      "grant",
      "grant_status",
      "Grant Status",
      "string",
      "Lifecycle state: OFFERED, ACCEPTED, LAPSED, WITHDRAWN, etc."
    ),
    col(
      "grant",
      "granted_options",
      "Granted Options",
      "number",
      "Total number of options/units in this grant."
    ),
    col(
      "grant",
      "strike_price",
      "Strike Price",
      "currency",
      "Exercise price per option. Zero for RSUs."
    ),
    col(
      "grant",
      "strike_price_type",
      "Strike Price Type",
      "string",
      "How the strike price was set: OPTIONS_PRICE, FMV, etc."
    ),
    col(
      "grant",
      "vesting_start_date",
      "Vesting Start Date",
      "date",
      "Date from which vesting tranches are calculated."
    ),
    col(
      "grant",
      "last_date_to_exercise",
      "Last Date to Exercise",
      "date",
      "Deadline by which vested options must be exercised."
    ),
    col(
      "grant",
      "vesting_schedule_name",
      "Vesting Schedule",
      "string",
      "Name of the vesting template applied to this grant."
    ),
    col(
      "grant",
      "grant_from_pool",
      "Granted From Pool",
      "string",
      "Which option pool this grant was drawn from."
    ),
    col(
      "grant",
      "conversion_methodology",
      "Conversion Methodology",
      "string",
      "How fractional options are handled on conversion: ROUND_DOWN, ROUND_UP, etc."
    ),
    col(
      "grant",
      "is_vesting_paused",
      "Vesting Paused",
      "boolean",
      "True if vesting has been paused for this grant."
    ),
    col(
      "grant",
      "actual_vesting_day",
      "Actual Vesting Day Rule",
      "string",
      "Whether vesting happens on SAME_DAY or next business day."
    ),
    col(
      "grant",
      "grant_ratio",
      "Grant Ratio",
      "number",
      "Ratio used when this is a bonus / corporate action grant."
    ),
    col(
      "grant",
      "grant_price_per_option",
      "Price Per Option",
      "currency",
      "Custom per-option value override, if applicable."
    ),
    col(
      "grant",
      "grant_note",
      "Grant Note",
      "string",
      "Admin notes attached to this grant."
    ),
  ]),

  // ── Computed ─────────────────────────────────────────────────────────────
  table(
    "computed",
    "Computed Columns",
    "Pre-calculated fields derived from grant + transaction data",
    [
      col(
        "computed",
        "vested",
        "Total Vested",
        "number",
        "Total options that have vested to date."
      ),
      col(
        "computed",
        "unvested",
        "Unvested",
        "number",
        "Options yet to vest (Granted − Vested)."
      ),
      col(
        "computed",
        "net_vested",
        "Net Vested",
        "number",
        "Options vested but not yet exercised (Vested − Exercised)."
      ),
      col(
        "computed",
        "stock_exercised",
        "Stock Exercised",
        "number",
        "Options settled as shares via stock exercise."
      ),
      col(
        "computed",
        "cashed_out",
        "Cashed Out",
        "number",
        "Options settled in cash."
      ),
      col(
        "computed",
        "exercised_total",
        "Total Exercised",
        "number",
        "Total exercised options — stock + cash combined."
      ),
      col(
        "computed",
        "lapsed",
        "Lapsed",
        "number",
        "Options that expired without being exercised."
      ),
      col(
        "computed",
        "surrendered",
        "Surrendered / Forfeited",
        "number",
        "Options voluntarily surrendered or forfeited on exit."
      ),
      col(
        "computed",
        "outstanding",
        "Outstanding Options",
        "number",
        "Active options: Granted − Exercised − Lapsed − Surrendered."
      ),
      col(
        "computed",
        "value_at_grant",
        "Value at Grant",
        "currency",
        "Intrinsic value at grant date: Granted Options × Strike Price."
      ),
      col(
        "computed",
        "percentage_vested",
        "% Vested",
        "percentage",
        "Percentage of total granted options that have vested."
      ),
      col(
        "computed",
        "percentage_exercised",
        "% Exercised",
        "percentage",
        "Percentage of total granted options that have been exercised."
      ),
      col(
        "computed",
        "grant_count",
        "Grant Count",
        "number",
        "Number of grants aggregated into this row (useful when grain = employee or plan)."
      ),
      col(
        "computed",
        "total_granted_options",
        "Sum: Granted Options",
        "number",
        "Aggregate sum of granted options — useful for pivot tables."
      ),
      col(
        "computed",
        "total_vested",
        "Sum: Vested",
        "number",
        "Aggregate sum of vested options — useful for pivot tables."
      ),
      col(
        "computed",
        "total_outstanding",
        "Sum: Outstanding",
        "number",
        "Aggregate sum of outstanding options — useful for pivot tables."
      ),
      col(
        "computed",
        "avg_strike_price",
        "Avg: Strike Price",
        "currency",
        "Weighted average strike price across aggregated grants."
      ),
      col(
        "computed",
        "total_value_at_grant",
        "Sum: Value at Grant",
        "currency",
        "Aggregate sum of value at grant — useful for pivot tables."
      ),
    ]
  ),

  // ── Vesting Tranche (grain = vesting_tranche) ─────────────────────────────
  table(
    "vesting",
    "Vesting Tranche",
    "Per-tranche details — available when report basis is 'Vesting Tranche'",
    [
      col(
        "vesting",
        "vesting_date",
        "Vesting Date",
        "date",
        "Date this tranche is scheduled to vest."
      ),
      col(
        "vesting",
        "vesting_state",
        "Vesting State",
        "string",
        "State of this tranche: ACTIVE, VESTED, LAPSED, etc."
      ),
      col(
        "vesting",
        "options_in_tranche",
        "Options in Tranche",
        "number",
        "Number of options vesting in this specific tranche."
      ),
      col(
        "vesting",
        "vesting_percentage",
        "Tranche %",
        "percentage",
        "This tranche as a percentage of the total grant."
      ),
      col(
        "vesting",
        "cumulative_vested",
        "Cumulative Vested",
        "number",
        "Running total of vested options up to and including this tranche."
      ),
      col(
        "vesting",
        "trigger_type",
        "Trigger Type",
        "string",
        "What triggers this tranche: TIME, MILESTONE, etc."
      ),
      col(
        "vesting",
        "vesting_schedule_id",
        "Schedule ID",
        "string",
        "Internal ID of the vesting schedule this tranche belongs to."
      ),
    ]
  ),

  // ── Transaction (grain = transaction) ────────────────────────────────────
  table(
    "transaction",
    "Transaction",
    "Per-transaction details — available when report basis is 'Transaction'",
    [
      col(
        "transaction",
        "transaction_type",
        "Transaction Type",
        "string",
        "Type of transaction: STOCK_EXERCISE, CASHED_OUT, LAPSED, SURRENDERED, etc."
      ),
      col(
        "transaction",
        "transaction_date",
        "Transaction Date",
        "date",
        "Date on which the transaction was processed."
      ),
      col(
        "transaction",
        "transaction_options",
        "Transaction Options",
        "number",
        "Number of options involved in this transaction."
      ),
      col(
        "transaction",
        "transaction_state",
        "Transaction State",
        "string",
        "Processing state of the transaction: COMPLETED, PENDING, etc."
      ),
      col(
        "transaction",
        "transaction_fmv",
        "FMV at Transaction",
        "currency",
        "Fair market value per option at the time of the transaction."
      ),
      col(
        "transaction",
        "transaction_event_type",
        "Event Type",
        "string",
        "The event that triggered this transaction, if any."
      ),
      col(
        "transaction",
        "transaction_notes",
        "Transaction Notes",
        "string",
        "Admin notes recorded at the time of the transaction."
      ),
    ]
  ),
];

// ─── Static preview rows ──────────────────────────────────────────────────────
// Used by ReportCanvas for a 5-row placeholder before real data is fetched.

export const MOCK_PREVIEW_ROWS: PreviewRow[] = [
  {
    employee_name: "Alice Sharma",
    hr_id: "EMP001",
    email: "alice@acme.com",
    status: "Active",
    employment_type: "Full-time",
    date_of_joining: "2021-03-15",
    department: "Engineering",
    designation: "Senior Engineer",
    plan_name: "ESOP 2021",
    plan_type: "ESOP",
    grant_identifier: "GRT-001",
    grant_type: "ESOP",
    grant_date: "2021-04-01",
    grant_status: "ACCEPTED",
    granted_options: 1000,
    strike_price: 10,
    vested: 500,
    unvested: 500,
    net_vested: 400,
    stock_exercised: 100,
    cashed_out: 0,
    exercised_total: 100,
    lapsed: 0,
    surrendered: 0,
    outstanding: 900,
    value_at_grant: 10000,
    percentage_vested: 50,
  },
  {
    employee_name: "Bob Mehta",
    hr_id: "EMP002",
    email: "bob@acme.com",
    status: "Active",
    employment_type: "Full-time",
    date_of_joining: "2020-07-01",
    department: "Product",
    designation: "Product Manager",
    plan_name: "ESOP 2020",
    plan_type: "ESOP",
    grant_identifier: "GRT-002",
    grant_type: "ESOP",
    grant_date: "2020-08-01",
    grant_status: "ACCEPTED",
    granted_options: 2000,
    strike_price: 8,
    vested: 1500,
    unvested: 500,
    net_vested: 1200,
    stock_exercised: 300,
    cashed_out: 0,
    exercised_total: 300,
    lapsed: 0,
    surrendered: 0,
    outstanding: 1700,
    value_at_grant: 16000,
    percentage_vested: 75,
  },
  {
    employee_name: "Carol Das",
    hr_id: "EMP003",
    email: "carol@acme.com",
    status: "Inactive",
    employment_type: "Full-time",
    date_of_joining: "2019-01-10",
    department: "Sales",
    designation: "Sales Lead",
    plan_name: "ESOP 2019",
    plan_type: "ESOP",
    grant_identifier: "GRT-003",
    grant_type: "ESOP",
    grant_date: "2019-02-01",
    grant_status: "LAPSED",
    granted_options: 800,
    strike_price: 5,
    vested: 800,
    unvested: 0,
    net_vested: 0,
    stock_exercised: 0,
    cashed_out: 0,
    exercised_total: 0,
    lapsed: 800,
    surrendered: 0,
    outstanding: 0,
    value_at_grant: 4000,
    percentage_vested: 100,
  },
  {
    employee_name: "David Nair",
    hr_id: "EMP004",
    email: "david@acme.com",
    status: "Active",
    employment_type: "Contractor",
    date_of_joining: "2022-06-15",
    department: "Design",
    designation: "UX Designer",
    plan_name: "ESOP 2022",
    plan_type: "RSU",
    grant_identifier: "GRT-004",
    grant_type: "RSU",
    grant_date: "2022-07-01",
    grant_status: "ACCEPTED",
    granted_options: 500,
    strike_price: 0,
    vested: 125,
    unvested: 375,
    net_vested: 125,
    stock_exercised: 0,
    cashed_out: 0,
    exercised_total: 0,
    lapsed: 0,
    surrendered: 0,
    outstanding: 500,
    value_at_grant: 0,
    percentage_vested: 25,
  },
  {
    employee_name: "Eva Pillai",
    hr_id: "EMP005",
    email: "eva@acme.com",
    status: "Active",
    employment_type: "Full-time",
    date_of_joining: "2023-01-02",
    department: "Finance",
    designation: "Finance Analyst",
    plan_name: "ESOP 2022",
    plan_type: "ESOP",
    grant_identifier: "GRT-005",
    grant_type: "ESOP",
    grant_date: "2023-02-01",
    grant_status: "OFFERED",
    granted_options: 600,
    strike_price: 12,
    vested: 0,
    unvested: 600,
    net_vested: 0,
    stock_exercised: 0,
    cashed_out: 0,
    exercised_total: 0,
    lapsed: 0,
    surrendered: 0,
    outstanding: 600,
    value_at_grant: 7200,
    percentage_vested: 0,
  },
];
